import { z } from 'zod'

export const NodeTypeSchema = z.enum([
  'prompt',
  'text-to-image',
  'image-to-video',
  'output',
  'static-image',
])

export const PortTypeSchema = z.enum(['text', 'image', 'video', 'number', 'enum'])

export const PortDefinitionSchema = z.object({
  key: z.string().min(1),
  type: PortTypeSchema,
  label: z.string().optional(),
  enumValues: z.array(z.string()).optional(),
})

export const WorkflowNodeSchema = z.object({
  id: z.string().min(1),
  type: NodeTypeSchema,
  label: z.string(),
  inputs: z.array(PortDefinitionSchema),
  outputs: z.array(PortDefinitionSchema),
  position: z.object({ x: z.number(), y: z.number() }),
  config: z.record(z.string(), z.unknown()).optional(),
})

export const WorkflowEdgeSchema = z.object({
  id: z.string().min(1),
  sourceNodeId: z.string().min(1),
  sourceOutputKey: z.string().min(1),
  targetNodeId: z.string().min(1),
  targetInputKey: z.string().min(1),
})

export const SCHEMA_VERSIONS = ['1.0.0'] as const
export type SchemaVersion = (typeof SCHEMA_VERSIONS)[number]
export const CURRENT_SCHEMA_VERSION: SchemaVersion = '1.0.0'

export const WorkflowSchema = z
  .object({
    id: z.string().min(1),
    schemaVersion: z.enum(SCHEMA_VERSIONS),
    name: z.string().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    nodes: z.array(WorkflowNodeSchema),
    edges: z.array(WorkflowEdgeSchema),
  })
  .refine(
    (workflow) => {
      const nodeIds = new Set(workflow.nodes.map((n) => n.id))
      return workflow.edges.every((e) => nodeIds.has(e.sourceNodeId) && nodeIds.has(e.targetNodeId))
    },
    { message: 'Edge references a non-existent node ID' },
  )

export type Workflow = z.infer<typeof WorkflowSchema>
export type WorkflowNode = z.infer<typeof WorkflowNodeSchema>
export type WorkflowEdge = z.infer<typeof WorkflowEdgeSchema>
export type NodeType = z.infer<typeof NodeTypeSchema>
export type PortType = z.infer<typeof PortTypeSchema>
export type PortDefinition = z.infer<typeof PortDefinitionSchema>

export function migrate(raw: unknown): Workflow {
  const { schemaVersion } = z.object({ schemaVersion: z.string() }).parse(raw)

  switch (schemaVersion) {
    case '1.0.0':
      return WorkflowSchema.parse(raw)
    default:
      throw new Error(`Unknown schema version: ${schemaVersion}`)
  }
}
